import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { zpcAdminLinks } from './zpc-admin-links';
import { zpmAdminLinks } from './zpm-admin-links';
import { SystemStatusContext } from '../shared/contexts/system-status.context';
import {
  CurrentUser,
  EmailSettings,
  HealthStatus,
  PrivilegedTenant,
  State,
  StatusCategory,
  SystemHealthStatus,
  ZpmPrivilege,
} from '../shared/models/generated/ziroModels';
import { Subscription } from 'rxjs';
import { EmailSettingsContext } from '../shared/contexts/email-settings.context';
import { ZiroIcons } from '../shared/models/ziro-icons.enum';
import { AuthenticationContext } from '../shared/contexts/authentication.context';
import { GlobalPropertiesContext } from '../shared/contexts/global-properties.context';
import { NavProps, NavSection, NavSectionLink } from '../shared/side-navigation/side-navigation.model';
import { HelpdeskOptionsContext } from '../helpdesk/shared/contexts/helpdesk-options.context';
import { CustomUiOptionsContext } from '../shared/contexts/custom-ui-options.context';
import { BandwidthSettingsContext } from '../shared/contexts/bandwidth-settings.context';
import { ProxyKeysContext } from './contexts/proxy-keys.context';
import { ProxyConnectionSettingsContext } from './contexts/proxy-connection-settings.context';
import { zpcProxyServerAdminLinks } from './zpc-proxy-server-admin-links';
import { MicrosoftTenantsStateContext } from '../shared/contexts/microsoft-tenants-state.context';

interface DisabledConfig {
  isDisabled: (flags: NavProps) => boolean;
  disabledTooltip: (flags: NavProps) => string | null;
}

@Component({
  selector: 'ziro-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
  standalone: false,
})
export class AdminComponent implements OnInit, OnDestroy {
  private systemStatusContext = inject(SystemStatusContext);
  private emailSettingsContext = inject(EmailSettingsContext);
  private authenticationContext = inject(AuthenticationContext);
  private globalPropertiesContext = inject(GlobalPropertiesContext);
  private _microsoftTenantsStateContext = inject(MicrosoftTenantsStateContext);
  private bandwidthSettingsContext = inject(BandwidthSettingsContext);
  private _helpdeskOptionsContext = inject(HelpdeskOptionsContext);
  private _uiOptionsContext = inject(CustomUiOptionsContext);
  private _zpmProxyKeysContext = inject(ProxyKeysContext);
  private _zpcProxyKeysContext = inject(ProxyConnectionSettingsContext);

  zpcAdminLinksSignal = signal<NavSection[]>(zpcAdminLinks);
  tenants = signal<PrivilegedTenant[]>([]);
  navProps = signal<NavProps>({
    isLicenseExpired: false,
    hasEmailSettings: false,
    isHostedEnabled: false,
    isReportingModuleEnabled: false,
    isAutomationModuleEnabled: false,
    isHighAvailabilityEnabled: false,
    endpointDisabled: false,
    portingEnabled: false,
    isMsDialPlanConfigured: false,
    isZpmProxyKeyConfigured: false,
    isZpcProxyKeyConfigured: false,
  });
  systemStatus = signal<SystemHealthStatus | null>(null);
  currentUser = signal<CurrentUser>(null);

  ziroIcons = ZiroIcons;
  isProxyServer: boolean;
  proxyServerLinks = zpcProxyServerAdminLinks;

  zpmAdminLinksWithTenants = computed(() => {
    const tenants = this.tenants();
    const links: NavSection[] = [];
    const currentUser = this.currentUser();
    if (currentUser && this.authenticationContext.userHasPrivilege(currentUser, ZpmPrivilege.GLOBAL_SETTINGS)) {
      links.push(...zpmAdminLinks);
    }

    if (tenants.length > 0) {
      const tenantSections = this._createTenantSections(tenants);
      links.push(...tenantSections);
    }
    return links;
  });

  adminLinks = computed(() => {
    const props = this.navProps();
    return props.isHostedEnabled ? this.zpmAdminLinksWithTenants() : this.zpcAdminLinksSignal();
  });

  private _subs = new Subscription();

  ngOnInit(): void {
    const globalPropertiesSub = this.globalPropertiesContext.state$.subscribe((globalProperties) => {
      this.navProps.update((props) => ({
        ...props,
        isHostedEnabled: globalProperties.hostedEnabled,
      }));
    });

    const systemStatusSub = this.systemStatusContext.state$.subscribe((systemStatus: SystemHealthStatus) => {
      this.systemStatus.set(systemStatus);

      this.navProps.update((props) => ({
        ...props,
        endpointDisabled: systemStatus.endpointDisabled,
      }));

      systemStatus.healthStatuses?.forEach((status: HealthStatus) => {
        if (status.category === StatusCategory.LICENSES) {
          this.navProps.update((props) => ({
            ...props,
            isLicenseExpired: status.state === State.ERROR && status.cause === 'License is expired',
          }));
        }

        if (status.category === StatusCategory.TRAFFIC && status.state === State.ERROR) {
          this.navProps.update((props) => ({
            ...props,
            isInactiveSide: status.cause === 'This side is inactive',
          }));
        }
      });
    });

    const emailSettingsSub = this.emailSettingsContext.state$.subscribe((state: EmailSettings) => {
      this.navProps.update((props) => ({
        ...props,
        hasEmailSettings: !!state.host && !!state.from,
      }));
    });

    const authSub = this.authenticationContext.state$.subscribe((currentUser) => {
      this.currentUser.set(currentUser);
    });

    const uiOptionSub = this._uiOptionsContext.state$.subscribe((uiOptions) => {
      this.navProps.update((props) => ({
        ...props,
        isReportingModuleEnabled: uiOptions.reportingEnabled,
        isAutomationModuleEnabled: uiOptions.automationEnabled,
        isHighAvailabilityEnabled: uiOptions.highAvailabilityEnabled,
      }));
      this.isProxyServer = uiOptions.proxyServerOnlyModeEnabled && !uiOptions.provisioningEnabled;
    });

    const msTenantsCtxSub = this._microsoftTenantsStateContext.fieldConfigState$.subscribe((msTenants) => {
      this.tenants.set(msTenants.privilegedTenants);
    });

    const helpdeskOptionsSub = this._helpdeskOptionsContext.state$.subscribe((helpdeskOptions) => {
      this.navProps.update((props) => ({
        ...props,
        portingEnabled:
          this._helpdeskOptionsContext.isMicrosoftHelpdeskOptions(helpdeskOptions) && helpdeskOptions.portingEnabled,
      }));
    });

    const bandwidthSettingsSub = this.bandwidthSettingsContext.state$.subscribe((state) => {
      this.navProps.update((props) => ({
        ...props,
        isBandwidthConfigured: this.bandwidthSettingsContext.isBandwidthConfigured(state),
      }));
    });

    const zpmProxyKeysSub = this._zpmProxyKeysContext.state$.subscribe((proxyKey) => {
      this.navProps.update((props) => ({
        ...props,
        isZpmProxyKeyConfigured: !!proxyKey.keyFirstThree,
      }));
    });

    const zpcProxyKeysSub = this._zpcProxyKeysContext.state$.subscribe((proxyKey) => {
      this.navProps.update((props) => ({
        ...props,
        isZpcProxyKeyConfigured: proxyKey.host !== null,
      }));
    });

    this._subs.add(emailSettingsSub);
    this._subs.add(authSub);
    this._subs.add(systemStatusSub);
    this._subs.add(globalPropertiesSub);
    this._subs.add(msTenantsCtxSub);
    this._subs.add(helpdeskOptionsSub);
    this._subs.add(uiOptionSub);
    this._subs.add(bandwidthSettingsSub);
    this._subs.add(zpmProxyKeysSub);
    this._subs.add(zpcProxyKeysSub);
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  private _createTenantSections(tenants: PrivilegedTenant[]): NavSection[] {
    return tenants.map((tenant) => {
      const hasEndpointWarningState = this._hasEndpointIncorrectStateForTenant(tenant.tenantName, State.WARNING);
      const hasEndpointErrorState = this._hasEndpointIncorrectStateForTenant(tenant.tenantName, State.ERROR);

      let statusState: 'error' | 'warning' | null = null;
      if (hasEndpointWarningState) {
        statusState = 'warning';
      }
      if (hasEndpointErrorState) {
        statusState = 'error';
      }

      return {
        icon: ZiroIcons.MICROSOFT_365_SMALL,
        title: tenant.tenantName,
        dataAutomation: `tenant-${tenant.tenantId}-section-header`,
        links: this._createTenantLinks(tenant.tenantId),
        sideNavBadge: {
          statusState: statusState,
          badgeDataAutomation: statusState ? `tenant-${tenant.tenantId}-${statusState}` : '',
          tooltip: this._getHealthStatusCause(tenant.tenantName) || '',
        },
      };
    });
  }

  private _createTenantLinks(tenantId: any): NavSectionLink[] {
    const basePath = `/admin/tenants/${tenantId}`;

    return [
      this._createTenantLink(
        `${basePath}/app-registration`,
        'tkey;admin.tenants.app_registration.config.title',
        `menu-tenant-${tenantId}-config-link`,
        this._getStandardDisabledConfig(),
      ),
      this._createTenantLink(
        `${basePath}/security-group-management`,
        'tkey;admin.microsoft.security_group_management.title',
        `menu-tenant-${tenantId}-security-group-link`,
        this._getStandardDisabledConfig(),
      ),
      this._createTenantLink(
        `${basePath}/license-management`,
        'tkey;admin.license_management.title',
        `menu-tenant-${tenantId}-license-management-link`,
        this._getStandardDisabledConfig(),
      ),
      this._createTenantLink(
        `${basePath}/dial-plan-management`,
        'tkey;admin.msdialplan.management.title',
        `menu-tenant-${tenantId}-dial-plan-management-link`,
        this._getStandardDisabledConfig(),
      ),
      this._createTenantLink(
        `${basePath}/dial-plan-exception-management`,
        'tkey;admin.ms_dial_plan_exception.title',
        `menu-tenant-${tenantId}-dial-plan-exception-link`,
        this._getStandardDisabledConfig(),
      ),
      this._createTenantLink(
        `${basePath}/calling-product-thresholds`,
        'tkey;admin.microsoft.calling_product_thresholds.title',
        `menu-tenant-${tenantId}-calling-product-thresholds-link`,
        this._getStandardDisabledConfig(),
      ),
    ];
  }

  private _createTenantLink(
    link: string,
    text: string,
    dataAutomation: string,
    disabledConfig: DisabledConfig,
    isVisible?: (flags: NavProps) => boolean,
  ): NavSectionLink {
    const tenantLink: NavSectionLink = {
      link,
      text,
      dataAutomation,
      isDisabled: disabledConfig.isDisabled,
      disabledTooltip: disabledConfig.disabledTooltip,
    };

    if (isVisible) {
      tenantLink.isVisible = isVisible;
    }

    return tenantLink;
  }

  private _getStandardDisabledConfig(): DisabledConfig {
    return {
      isDisabled: (flags) => flags.isLicenseExpired,
      disabledTooltip: (flags) => (flags.isLicenseExpired ? 'tkey;admin.menu.license_expired.tooltip' : null),
    };
  }

  private _hasEndpointIncorrectStateForTenant(tenantName: string, state: State): boolean {
    const systemStatus = this.systemStatus();
    if (!systemStatus?.healthStatuses) {
      return false;
    }

    return systemStatus.healthStatuses.some(
      (status: HealthStatus) =>
        status.category === StatusCategory.ENDPOINTS &&
        status.state === state &&
        status.description?.includes(tenantName),
    );
  }

  private _getHealthStatusCause(tenantName: string): string {
    const systemStatus = this.systemStatus();
    if (!systemStatus?.healthStatuses) {
      return '';
    }

    return systemStatus.healthStatuses.find((status: HealthStatus) => status.description?.includes(tenantName))?.cause;
  }
}
